import {Component, ElementRef, OnDestroy, OnInit} from '@angular/core';
import {BusinessDataService} from '../../../../../data-repositories/business-data.service';
import {User} from '../../../../../models/user';
import {Investment} from '../../../../../models/investment';
import {SourceOfWealth} from '../../../../../models/employment';
import {SourceOfWealthData} from '../../../../../data-repositories/source-of-wealth-data';
import {ValidationInfoService} from '../../../../../repositories/validation-info.service';
import {Subscription} from 'rxjs';
import {StagesNavigationService} from '../../../stages-navigation/stages-navigation.service';

@Component({
  standalone: false,
  selector: 'app-source-of-wealth',
  templateUrl: './source-of-wealth.component.html',
  styleUrls: ['./source-of-wealth.component.scss']
})
export class SourceOfWealthComponent implements OnInit, OnDestroy {
  userModel: User;
  submitted: boolean = false;
  private subscription: Subscription;
  private userSubscription: Subscription;

  constructor(
    private service: BusinessDataService,
    private sourceOfWealthData: SourceOfWealthData,
    private validationService: ValidationInfoService,
    public navigationService: StagesNavigationService,
    private hostRef: ElementRef,
  ) {
    this.userSubscription = this.service.GetUser().subscribe((s) => {
      this.userModel = s.model;
      this.setDefaultValues();
    });
  }

  ngOnInit() {
    this.subscription = this.validationService.onUserUpdate.subscribe(() => {
      this.submitted = true;

      const isValid = this.sourceIsValid() &&
        !this.hasZeroSource() &&
        !this.hasEmptyDescription();

      this.validationService.validate(isValid, 'SourceOfWealthComponent');

      if (!isValid) {
        setTimeout(() => {
          const errors: HTMLElement[] = Array.from(
            this.hostRef.nativeElement.querySelectorAll('.error')
          );
          const visibleError = errors.find(e => !!e.offsetParent);
          visibleError?.scrollIntoView({behavior: 'smooth', block: 'center'});
        });
      }
    });
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
    this.userSubscription?.unsubscribe();
  }

  canToggleIncomeSource(source: SourceOfWealth) {
    const id = this.getSourceId(source);
    return !(id === 0 && this.isEmployed());
  }

  toggleIncomeSource(source: SourceOfWealth) {
    if (this.canToggleIncomeSource(source)) {
      source.isSource = !source.isSource;
      this.updateUser();
    }
  }

  getSourceFromId(id: number) {
    switch (id) {
      case 0:
        return this.userModel.employmentModel.incomeFormEmploymentSource;
      case 1:
        return this.userModel.employmentModel.disabilitySource;
      case 2:
        return this.userModel.employmentModel.inheritanceSource;
      case 3:
        return this.userModel.employmentModel.interestSource;
      case 4:
        return this.userModel.employmentModel.marketTradingProfitsSource;
      case 5:
        return this.userModel.employmentModel.pensionSource;
      case 6:
        return this.userModel.employmentModel.propertySource;
      case 7:
        return this.userModel.employmentModel.allowanceSource;
      case 8:
        return this.userModel.employmentModel.otherSource;
    }
  }

  getSourceId(source: SourceOfWealth) {
    if (source === this.userModel.employmentModel.incomeFormEmploymentSource) {
      return 0;
    } else if (source === this.userModel.employmentModel.disabilitySource) {
      return 1;
    } else if (source === this.userModel.employmentModel.inheritanceSource) {
      return 2;
    } else if (source === this.userModel.employmentModel.interestSource) {
      return 3;
    } else if (
      source === this.userModel.employmentModel.marketTradingProfitsSource
    ) {
      return 4;
    } else if (source === this.userModel.employmentModel.pensionSource) {
      return 5;
    } else if (source === this.userModel.employmentModel.propertySource) {
      return 6;
    } else if (source === this.userModel.employmentModel.allowanceSource) {
      return 7;
    } else if (source === this.userModel.employmentModel.otherSource) {
      return 8;
    } else {
      return -1;
    }
  }

  getSourceName(source: SourceOfWealth) {
    const id = this.getSourceId(source);
    return this.sourceOfWealthData.incomeSourceList.find((x) => x.id === id)
      .name;
  }

  requiredDescription(source: SourceOfWealth) {
    return source.isSource && this.getSourceId(source) === 8;
  }

  getSourceMax(source: SourceOfWealth) {
    return (
      100 -
      this.getIncomeSourceList()
        .filter((x) => x !== source && x.isSource)
        .map((x) => x.percent)
        .reduce((x, y) => x + y, 0)
    );
  }

  getSourceSum() {
    return this.getIncomeSourceList()
      .filter(x => x.isSource)
      .map((x) => x.percent)
      .reduce((x, y) => x + y, 0);
  }

  getIncomeSourceList() {
    return this.sourceOfWealthData.incomeSourceList
      .map((x) => this.getSourceFromId(x.id));
  }

  sourceIsValid() {
    return this.getSourceSum() === 100;
  }

  hasEmptyDescription() {
    return this.getIncomeSourceList().some(
      (x) => this.requiredDescription(x) && (!x.note || x.note.trim() === '')
    );
  }

  hasZeroSource() {
    return this.getIncomeSourceList().filter(x => x.isSource).some((x) => x.percent === 0);
  }

  public updateUser(): void {
    this.service.updateUserWithoutValidation(this.userModel, () => {
    });
  }

  private setDefaultValues() {
    const income = this.getSourceFromId(0);
    income.isSource = income.isSource || this.isEmployed();
    if (income.isSource && this.getSourceSum() === 0) {
      income.percent = this.getSourceSum() === 0 ? 100 : 0;
    }
  }

  private isEmployed(): boolean {
    return (
      this.userModel.employmentModel.employmentStatus === 'EMPLOYED' ||
      this.userModel.employmentModel.employmentStatus === 'SELFEMPLOYED'
    );
  }
}
